import { useCallback } from 'react';
import { LOTTO_CONSTANTS } from '../constants';

export const useRandomBallGeneration = ({
  ticket,
  editedNumbers,
  setEditedNumbers,
  setValidationError,
  minNormalCount,
  requiredBonusCount,
}) => {
  const handleRandomFill = useCallback(() => {
    const hasBalls = ticket.balls != null;
    if (!hasBalls) return;

    const currentNormalCount = editedNumbers.filter((n) => n.type === 'NORMAL').length;
    const currentBonusCount = editedNumbers.filter((n) => n.type === 'BONUS').length;

    const normalCount = currentNormalCount > 0 ? currentNormalCount : minNormalCount;
    const bonusCount = currentBonusCount > 0 ? currentBonusCount : requiredBonusCount;

    if (normalCount > ticket.main_grid_total || ticket.main_grid_total <= 0) {
      setValidationError({
        index: 0,
        message: `Normal balls exceed grid size (${ticket.main_grid_total})`,
      });
      return;
    }

    if (bonusCount > 0 && ticket.secondary_grid_total <= 0) {
      setValidationError({
        index: normalCount,
        message: 'Bonus grid size is zero',
      });
      return;
    }

    if (bonusCount > ticket.secondary_grid_total) {
      setValidationError({
        index: normalCount,
        message: `Bonus balls exceed grid size (${ticket.secondary_grid_total})`,
      });
      return;
    }

    const normalSet = new Set();
    let safety = 0;
    while (normalSet.size < normalCount) {
      const value = Math.floor(Math.random() * ticket.main_grid_total) + 1;
      normalSet.add(value);
      safety += 1;
      if (safety > LOTTO_CONSTANTS.SAFETY_ITERATION_LIMIT) {
        setValidationError({ index: 0, message: 'Unable to generate unique normal balls' });
        return;
      }
    }

    const bonusValues = [];
    safety = 0;
    while (bonusValues.length < bonusCount) {
      const value = Math.floor(Math.random() * ticket.secondary_grid_total) + 1;
      const duplicateBonus = bonusValues.includes(value);
      const clashesWithNormal = ticket.secondary_unique && normalSet.has(value);
      if (duplicateBonus || clashesWithNormal) continue;
      bonusValues.push(value);
      safety += 1;
      if (safety > LOTTO_CONSTANTS.SAFETY_ITERATION_LIMIT) {
        setValidationError({ index: normalCount, message: 'Unable to generate unique bonus balls' });
        return;
      }
    }

    const normalNumbers = Array.from(normalSet).map((value) => ({ type: 'NORMAL', value }));
    const bonusNumbers = bonusValues.map((value) => ({ type: 'BONUS', value }));
    const nextNumbers = [...normalNumbers, ...bonusNumbers];

    setEditedNumbers(nextNumbers);
    setValidationError(null);
  }, [ticket, editedNumbers, setEditedNumbers, setValidationError, minNormalCount, requiredBonusCount]);

  return { handleRandomFill };
};
